/* eslint-disable class-methods-use-this */
import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CardConfig } from '@affinis/smartus-components/lib/card/card.component.types';
import { FlatTreeControl } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
  MatTreeModule,
} from '@angular/material/tree';
import { Router } from '@angular/router';
import {
  ArchObjTreeViewModel,
  BillingAreaClient,
  BillingAreaNavigationViewmodel,
  BillingAreaUsageDetailsViewmodel,
} from '@core/api';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { LocationFormlyFieldService } from '@core/services/formly-service/location-formly-field.service';
import { SessionStorageService } from '@core/services/session-storage-service/session-storage.service';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OverrideModule } from '@override/override.module';
import {
  GANTT_GLOBAL_CONFIG,
  GanttDate,
  GanttGroup,
  GanttItem,
  GanttViewOptions,
  GanttViewType,
  NgxGanttComponent,
  NgxGanttModule,
} from '@worktile/gantt';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { AppGanttFlatComponent } from '../costcenterusage/flat/flat.component';
import { EditAccountingAreasColumnUsageSharesComponent } from './edit-accounting-areas-column-usage-shares/edit-accounting-areas-column-usage-shares.component';
import { EditAccountingAreasUsageSharesComponent } from './edit-accounting-areas-usage-shares/edit-accounting-areas-usage-shares.component';
import { EditAccountingAreasComponent } from './edit-accounting-areas/edit-accounting-areas.component';

interface IBillingAreaNavigationViewmodel
  extends BillingAreaNavigationViewmodel {
  expandable: boolean;
  level: number;
  fixedNodes: IUsageViewModel[];
  type?: string;
}

interface IUsageViewModel {
  id: string;
  vbId: string;
  title: string;
  billingAreaTitle?: string;
}

interface IGanttItem extends GanttItem {
  roomTitle?: string;
  workspaceTitle?: string;
  costCenterId?: string;
  areaString?: string;
}

/** Flat node with expandable and level information */
interface FlatNode {
  expandable: boolean;
  name: string;
  level: number;
  id: string;
  type?: string;
  vbId?: string;
  billingAreaTitle?: string;
}

@Component({
  selector: 'app-accounting_areas',
  standalone: true,
  imports: [
    CommonModule,
    SmartusComponentsModule,
    TranslateModule,
    OverrideModule,
    FormlyModule,
    FormsModule,
    ReactiveFormsModule,
    MatTooltipModule,
    NgxGanttModule,
    MatDialogModule,
    MatTreeModule,
    MatIconModule,
    MatButtonModule,
    AppGanttFlatComponent,
  ],
  templateUrl: './accounting-areas.component.html',
  styleUrl: './accounting-areas.component.scss',
  providers: [
    {
      provide: GANTT_GLOBAL_CONFIG,
      useValue: {
        dateFormat: {
          hour: 'HH:mm',
          day: 'dd. MMM yyyy',
          week: 'w',
          month: 'LLL',
          quarter: 'QQQ',
          year: 'yyyy',
          yearMonth: 'LLL yyyy',
          yearQuarter: `QQQ yyyy`,
        },
      },
    },
  ],
})
export class AccountingAreasComponent implements OnInit {
  headline = this.translateService.instant(
    'menu.data_maintenance.component.accounting_areas'
  );
  form = new FormGroup({});

  formFields: Array<FormlyFieldConfig>;

  formModel = {};

  isLoading = false;

  selectedBillingAreas: IBillingAreaNavigationViewmodel[];

  selectedBillingArea: string;

  selectedBillingAreaId: string;

  private isBillingArea(
    node: BillingAreaNavigationViewmodel | ArchObjTreeViewModel
  ): node is BillingAreaNavigationViewmodel {
    return (node as BillingAreaNavigationViewmodel).id.includes('VB');
  }

  private isArchObj(
    node: BillingAreaNavigationViewmodel | ArchObjTreeViewModel
  ): node is ArchObjTreeViewModel {
    return (
      (node as ArchObjTreeViewModel).children !== undefined ||
      (node as ArchObjTreeViewModel).childrenCount !== undefined
    );
  }

  private isFixedNode(
    node: IBillingAreaNavigationViewmodel | ArchObjTreeViewModel
  ): node is IBillingAreaNavigationViewmodel {
    return (node as IBillingAreaNavigationViewmodel).id.startsWith('usages');
  }

  private readonly transformer = (
    node: IBillingAreaNavigationViewmodel | ArchObjTreeViewModel,
    level: number
  ): FlatNode => {
    let expandable = false;
    let name = '';
    let vbId = '';
    let billingAreaTitle = '';

    if (this.isBillingArea(node)) {
      expandable = true;
      name = node.billingAreaTitle || '';
    } else if (this.isArchObj(node)) {
      expandable = !!(node.children && node.children.length > 0);
      name = node.displayName || '';
    } else if (this.isFixedNode(node)) {
      name = (node as IUsageViewModel).title || '';
      vbId = (node as IUsageViewModel).vbId;
      billingAreaTitle = (node as IUsageViewModel).billingAreaTitle;
    }

    return {
      expandable,
      name,
      level,
      id: node.id || '',
      vbId,
      billingAreaTitle,
      type: node.type,
    };
  };

  treeControl = new FlatTreeControl<FlatNode>(
    node => node.level,
    node => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this.transformer,
    node => node.level,
    node => node.expandable,
    node => {
      if (this.isBillingArea(node)) {
        return [...node.fixedNodes, ...(node.buildings ?? [])];
      } else {
        return node.children || [];
      }
    }
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  cardArchitecture: CardConfig = {
    header: {
      title: {
        label: this.translateService.instant(
          'accountingArea.accounting_areas.architecture'
        ),
      },
    },
  };

  cardView: CardConfig = {
    header: {
      title: {
        label: this.translateService.instant(
          'accountingArea.accounting_areas.view'
        ),
      },
    },
  };

  items: IGanttItem[] = [];

  currentYear = new Date().getFullYear();

  viewOptions: GanttViewOptions = {
    start: new GanttDate(new Date(this.currentYear, 0, 1)),
    end: new GanttDate(new Date(this.currentYear, 11, 31)),
    min: new GanttDate(new Date(this.currentYear, 0, 1)),
    max: new GanttDate(new Date(this.currentYear, +1, 11, 31)),
    cellWidth: 80,
  };

  options = {
    viewType: GanttViewType.quarter,
    draggable: false,
    styles: {
      lineHeight: 50,
      barHeight: 20,
    },
  };

  views = [
    {
      name: this.translateService.instant(
        'accountingArea.accounting_areas.week'
      ),
      value: GanttViewType.week,
    },
    {
      name: this.translateService.instant(
        'accountingArea.accounting_areas.month'
      ),
      value: GanttViewType.month,
    },
    {
      name: this.translateService.instant(
        'accountingArea.accounting_areas.quarter'
      ),
      value: GanttViewType.quarter,
    },
  ];

  selectedViewType: GanttViewType = GanttViewType.month;

  isCustomStyleActive = false;

  buttonMaxWidthTitle = this.translateService.instant(
    'accountingArea.accounting_areas.max_width'
  );

  @ViewChild('gantt') ganttComponent: NgxGanttComponent;

  groups: GanttGroup[] = [];

  isStrategicBusinessUnitSelected: boolean = false;

  isCostcenterUsageSelected: boolean = false;

  activeToast: ActiveToast<object>;

  selectedLocation: string;

  expandNode: string;

  constructor(
    private readonly breadcrumbService: BreadcrumbService,
    private readonly translateService: TranslateService,
    private readonly fieldService: LocationFormlyFieldService,
    private readonly billingAreaClient: BillingAreaClient,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly sessionStorageService: SessionStorageService
  ) {
    this.breadcrumbService.setBreadcrumbs([
      {
        label: this.headline,
      },
    ]);
  }

  ngOnInit(): void {
    const defaultValue = this.sessionStorageService.get(
      'accountingAreaDefaultValue'
    );

    this.expandNode = this.sessionStorageService.get(
      'accountingAreaExpandNode'
    );

    this.sessionStorageService.remove('accountingAreaDefaultValue');

    this.formFields = [
      ...this.fieldService.autocompleteSearch(
        false,
        defaultValue ?? null,
        true,
        id => this.onSelect(id),
        null,
        () => this.onDelete(),
        this.translateService.instant(
          'location.shared.delete_location_search_tooltip'
        )
      ),
    ];
  }

  hasChild = (_: number, node: FlatNode) => node.expandable;

  public onSelect(archObjId: string): void {
    this.selectedLocation = archObjId;
    if (this.activeToast) {
      this.toastr.remove(this.activeToast.toastId);
    }
    this.isStrategicBusinessUnitSelected = false;
    this.isCostcenterUsageSelected = false;
    this.getTreeData(this.selectedLocation);
  }

  getTreeData(archObjId) {
    this.isLoading = true;
    this.billingAreaClient.getBillingAreaNavigation(archObjId).subscribe({
      next: (res: IBillingAreaNavigationViewmodel[]) => {
        this.selectedBillingAreas = res;
        this.isLoading = false;
        this.selectedBillingAreas = this.addFixedNodes(
          this.selectedBillingAreas
        );
        this.dataSource.data = this.selectedBillingAreas;

        this.expandPathToNode(this.expandNode);
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  onDelete(): () => void {
    this.selectedLocation = null;
    if (this.activeToast) {
      this.toastr.remove(this.activeToast.toastId);
    }
    this.selectedBillingAreas = [];
    this.dataSource.data = [];

    this.groups = [];
    this.items = [];
    this.isStrategicBusinessUnitSelected = false;
    this.isCostcenterUsageSelected = false;
    return null;
  }

  addFixedNodes(data: IBillingAreaNavigationViewmodel[]) {
    data.forEach(element => {
      const fixedNodes: IUsageViewModel[] = [
        {
          id: 'usagesSbu',
          vbId: element.id,
          title: this.translateService.instant(
            'accountingArea.accounting_areas.sbu_usages'
          ),
          billingAreaTitle: element.billingAreaTitle,
        },
        {
          id: 'usagesDetail',
          vbId: element.id,
          title: this.translateService.instant(
            'accountingArea.accounting_areas.detail_usages'
          ),
          billingAreaTitle: element.billingAreaTitle,
        },
      ];
      element.fixedNodes = fixedNodes;
    });
    return data;
  }

  findObjectByName(data, targetName, isChild?: boolean) {
    let obj;
    if (!isChild) {
      obj = data[0];
    } else {
      obj = data;
    }
    if (obj.displayName === targetName) {
      return obj;
    }

    if (obj.children && obj.children.length > 0) {
      for (const child of obj.children) {
        const result = this.findObjectByName(child, targetName, true);
        if (result) {
          return result;
        }
      }
    }

    return null;
  }

  getBillingAreaUsages(vbId: string) {
    this.isLoading = true;
    if (this.activeToast) {
      this.toastr.remove(this.activeToast.toastId);
    }

    this.billingAreaClient.getBillingAreaUsageTable(vbId).subscribe({
      next: res => {
        this.groups = res.groups as GanttGroup[];
        this.items = res.items as IGanttItem[];
        this.options.viewType = GanttViewType.month;
        this.isLoading = false;
      },
      error: () => {
        this.groups = [];
        this.items = [];
        this.isLoading = false;
        this.activeToast = this.toastr.error(
          this.translateService.instant('generic.errorMessage')
        );
      },
    });
  }

  getStrategicBusinessUnitUsages(vbId: string) {
    this.isLoading = true;
    if (this.activeToast) {
      this.toastr.remove(this.activeToast.toastId);
    }

    this.billingAreaClient.getStrategicBusinessUnitUsageTable(vbId).subscribe({
      next: res => {
        this.groups = res.groups as GanttGroup[];
        this.items = res.items as IGanttItem[];
        this.options.viewType = GanttViewType.month;
        this.isLoading = false;
      },
      error: () => {
        this.groups = [];
        this.items = [];
        this.isLoading = false;
        this.activeToast = this.toastr.error(
          this.translateService.instant('generic.errorMessage')
        );
      },
    });
  }

  getTooltip(item) {
    return `
      ${this.translateService.instant('accountingArea.accounting_areas.tooltip.quantity_workingspaces')}
      ${item.cntWorkspaces ?? '--'}\n
      ${this.translateService.instant('accountingArea.accounting_areas.tooltip.share_percentage')}
      ${item.areaPercentage != null ? `${item.areaPercentage} %` : '--'}\n
      ${this.translateService.instant('accountingArea.accounting_areas.tooltip.assigned_accounting_area')}
      ${item.billingAreaName ?? '--'}\n
      ${this.translateService.instant('accountingArea.accounting_areas.tooltip.costcenter')}
      ${item.costCenterId ?? '--'} \n
      ${this.translateService.instant('accountingArea.accounting_areas.tooltip.share')}
      ${item.areaUsed != null ? `${item.areaUsed} m²` : '--'}\n
      ${this.translateService.instant('accountingArea.accounting_areas.tooltip.validity')}
      ${this.formatUnixTimestamp(item.popupStart)} - ${this.formatUnixTimestamp(item.popupEnd)} \n`;
  }

  formatUnixTimestamp(unixTimestamp: number): string {
    const date = new Date(unixTimestamp * 1000);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  }

  barClick(e) {
    let editDialogRef = null;

    const usageDetails: (
      data: string
    ) => Observable<BillingAreaUsageDetailsViewmodel> = data => {
      let returnValue = null;
      if (this.isCostcenterUsageSelected) {
        returnValue = this.billingAreaClient.getBillingAreaUsageDetails(data);
      } else if (this.isStrategicBusinessUnitSelected) {
        returnValue =
          this.billingAreaClient.getBillingAreaSbuUsageDetails(data);
      }
      return returnValue;
    };

    usageDetails(e.item.id)?.subscribe({
      next: res => {
        if (this.isCostcenterUsageSelected) {
          editDialogRef = this.dialog.open(
            EditAccountingAreasUsageSharesComponent,
            {
              minWidth: '50vw',
              minHeight: '60vh',
              panelClass: 'smartportal-dialog',
              data: {
                title: this.translateService.instant(
                  'accountingArea.accounting_areas.edit_dialog.headline_costcenter_usage_edit'
                ),
                id: e.item.id,
                vb_name: res.title,
                vbId: this.selectedBillingAreaId,
                strategicBusinessUnit: res.strategicBusinessUnitId,
                costcenter: this.isCostcenterUsageSelected
                  ? (res.costCenterId ?? null)
                  : undefined,
                start: res.usageFrom,
                end: res.usageUntil,
                workspaceCount: res.workspaceCount,
                freeWorkspaceCount: res.freeWorkspaceCount,
                isEditMode: true,
                isDispo: e.item.isDispo,
              },
            }
          );
        } else if (this.isStrategicBusinessUnitSelected) {
          editDialogRef = this.dialog.open(
            EditAccountingAreasColumnUsageSharesComponent,
            {
              minWidth: '50vw',
              minHeight: '60vh',
              panelClass: 'smartportal-dialog',
              data: {
                title: this.translateService.instant(
                  'accountingArea.accounting_areas.column_usages.headline_column_usage_edit'
                ),
                id: e.item.id,
                vb_name: res.title,
                vbId: this.selectedBillingAreaId,
                sbuId: res.strategicBusinessUnitId,
                start: res.usageFrom,
                end: res.usageUntil,
                workspaceCount: res.workspaceCount,
                freeWorkspaceCount: res.freeWorkspaceCount,
                isEditMode: true,
                isDispo: e.item.isDispo,
              },
            }
          );
        }
        editDialogRef?.afterClosed().subscribe({
          next: (saved: boolean) => {
            if (saved) {
              if (this.isCostcenterUsageSelected) {
                this.getBillingAreaUsages(this.selectedBillingAreaId);
              } else if (this.isStrategicBusinessUnitSelected) {
                this.getStrategicBusinessUnitUsages(this.selectedBillingAreaId);
              }
            }
          },
        });
      },
    });
  }

  toggleStyle() {
    this.isCustomStyleActive = !this.isCustomStyleActive;
    if (this.isCustomStyleActive === true) {
      this.buttonMaxWidthTitle = this.translateService.instant(
        'accountingArea.accounting_areas.reset'
      );
    } else {
      this.buttonMaxWidthTitle = this.translateService.instant(
        'accountingArea.accounting_areas.max_width'
      );
    }
  }

  selectView(type: GanttViewType) {
    this.selectedViewType = type;
    this.options.viewType = type;
  }

  scrollToToday() {
    this.ganttComponent.scrollToToday();
  }

  onAddVerrechnungsbereich() {
    if (this.activeToast) {
      this.toastr.remove(this.activeToast.toastId);
    }
    const technicalPlace = '';
    const dialogRef = this.dialog.open(EditAccountingAreasComponent, {
      minWidth: '50vw',
      minHeight: '50vh',
      panelClass: 'smartportal-dialog',
      data: {
        title: this.translateService.instant(
          'accountingArea.accounting_areas.edit_dialog.headline_vb'
        ),
        technicalPlace, // Technischer Platz
        vbName: null, // VB-Bezeichnung
        totalWorkplaces: null, // Summe Arbeitsplätze (AP)
        occupiedWorkplaces: null, // Summe belegte Arbeitsplätze (AP belegt)
        freeWorkplaces: null, // Summe freie Arbeitsplätze (AP frei)
        areaInSquareMeters: null, // VB-Fläche in m²
        totalCosts: null, // VB-Gesamtkosten
        areaPerWorkplace: null, // Fläche pro Arbeitsplatz in m²
        costPerWorkplace: null,
        isEditMode: false,
        selectedLocation: this.selectedLocation,
      },
    });

    dialogRef.afterClosed().subscribe({
      next: res => {
        if (res) {
          this.getTreeData(this.selectedLocation);
        }
      },
    });
  }

  onSBUClick() {
    if (this.activeToast) {
      this.toastr.remove(this.activeToast.toastId);
    }
    const editDialogRef = this.dialog.open(
      EditAccountingAreasColumnUsageSharesComponent,
      {
        minWidth: '50vw',
        minHeight: '60vh',
        panelClass: 'smartportal-dialog',
        data: {
          title: this.translateService.instant(
            'accountingArea.accounting_areas.column_usages.headline_column_usage'
          ),
          vb_name: this.selectedBillingArea,
          vbId: this.selectedBillingAreaId,
          start: null,
          end: null,
        },
      }
    );

    editDialogRef.afterClosed().subscribe({
      next: res => {
        if (res) {
          this.getStrategicBusinessUnitUsages(this.selectedBillingAreaId);
        }
      },
    });
  }

  onKstNutzungsaClick() {
    if (this.activeToast) {
      this.toastr.remove(this.activeToast.toastId);
    }
    const editDialogRef = this.dialog.open(
      EditAccountingAreasUsageSharesComponent,
      {
        minWidth: '50vw',
        minHeight: '60vh',
        panelClass: 'smartportal-dialog',
        data: {
          title: this.translateService.instant(
            'accountingArea.accounting_areas.edit_dialog.headline_costcenter_usage'
          ),
          vb_name: this.selectedBillingArea,
          vbId: this.selectedBillingAreaId,
          start: null,
          end: null,
        },
      }
    );

    editDialogRef.afterClosed().subscribe({
      next: res => {
        if (res) {
          this.getBillingAreaUsages(this.selectedBillingAreaId);
        }
      },
    });
  }

  onEditVB(item) {
    this.isLoading = true;
    if (this.activeToast) {
      this.toastr.remove(this.activeToast.toastId);
    }

    this.billingAreaClient.getBillingAreaDetails(item.id).subscribe({
      next: details => {
        this.isLoading = false;
        const dialogRef = this.dialog.open(EditAccountingAreasComponent, {
          minWidth: '50vw',
          minHeight: '50vh',
          panelClass: 'smartportal-dialog',
          data: {
            title: this.translateService.instant(
              'accountingArea.accounting_areas.edit_dialog.headline_vb_edit'
            ),
            technicalPlace: details.id, // Technischer Platz
            vbName: item.name, // VB-Bezeichnung
            totalWorkplaces: details.cntWorkspace, // Summe Arbeitsplätze (AP)
            occupiedWorkplaces: details.cntWorkspaceUsed, // Summe belegte Arbeitsplätze (AP belegt)
            freeWorkplaces: details.cntWorkspaceFree, // Summe freie Arbeitsplätze (AP frei)
            areaInSquareMeters: details.area, // VB-Fläche in m²
            totalCosts: details.price, // VB-Gesamtkosten
            areaPerWorkplace: details.areaPerWorkspace, // Fläche pro Arbeitsplatz in m²
            costPerWorkplace: details.pricePerWorkspace,
            isEditMode: true,
            selectedLocation: this.selectedLocation,
            billingAreaId: item.id,
          },
        });

        dialogRef.afterClosed().subscribe({
          next: res => {
            if (res) {
              this.getTreeData(this.selectedLocation);
            }
          },
        });
      },
    });
  }

  onNodeClick(node) {
    const parts = this.router.url.split('/');
    const baseUrl = parts[0];
    if (node.vbId !== '' && node.level === 1) {
      if (node.id === 'usagesSbu') {
        this.getStrategicBusinessUnitUsages(node.vbId);
        this.isCostcenterUsageSelected = false;
        this.isStrategicBusinessUnitSelected = true;
        this.selectedBillingArea = node.billingAreaTitle;
        this.selectedBillingAreaId = node.vbId;
      } else if (node.id === 'usagesDetail') {
        this.getBillingAreaUsages(node.vbId);
        this.isStrategicBusinessUnitSelected = false;
        this.isCostcenterUsageSelected = true;
        this.selectedBillingArea = node.billingAreaTitle;
        this.selectedBillingAreaId = node.vbId;
      }
    } else if (node.type === 'ZRM') {
      window.open(baseUrl + '/edit-room/' + node.id, '_blank');
    } else if (node.type === 'ZAP') {
      window.open(baseUrl + '/working-space/' + node.id, '_blank');
    }
  }

  expandPathToNode(targetId: string) {
    const dataNodes = this.treeControl.dataNodes;
    const pathNodes: FlatNode[] = [];
    let currentNode = dataNodes.find(n => n.id === targetId);

    if (!currentNode) {
      return;
    }

    pathNodes.push(currentNode);

    while (currentNode.level > 0) {
      const currentNodeIndex = dataNodes.indexOf(currentNode);

      let parentNode: FlatNode | undefined = undefined;

      for (let i = currentNodeIndex - 1; i >= 0; i--) {
        if (dataNodes[i].level === currentNode.level - 1) {
          parentNode = dataNodes[i];
          break;
        }
      }

      if (parentNode) {
        pathNodes.push(parentNode);
        currentNode = parentNode;
      } else {
        break;
      }
    }

    pathNodes.reverse().forEach(n => this.treeControl.expand(n));
  }
}
